import colors from './colors'
import shadows from './shadows'

export const system = {
  colors,
  input: {
    sizes: {
      small: '32px',
      medium: '40px',
      large: '48px',
    },
  },
  avatar: {
    sizes: {
      xs: '24px',
      sm: '32px',
      md: '40px',
      lg: '48px',
      xl: '56px',
    },
    svg: {
      xs: '12px',
      sm: '14px',
      md: '18px',
      lg: '22px',
      xl: '26px',
    },
    dot: {
      xs: '6px',
      sm: '8px',
      md: '10px',
      lg: '12px',
      xl: '14px',
    },
    text: {
      initials: {
        xs: 'caption',
        sm: 'body2',
        md: 'body1',
        lg: 'heading6',
        xl: 'heading6',
      },
      name: {
        xs: 'body2',
        sm: 'body2',
        md: 'body1',
        lg: 'body1',
        xl: 'body1',
      },
      description: {
        xs: 'caption',
        sm: 'body2',
        md: 'body1',
        lg: 'body1',
        xl: 'body1',
      },
    },
  },
  buttonGroup: {
    backgrounds: {
      primary: colors.primary700,
      primaryGhost: colors.white,
      tab: colors.white,
      hover: {
        primary: colors.primary800,
        primaryGhost: colors.primary50,
        tab: colors.neutral100,
      },
    },
    fontColors: {
      primary: colors.cream50,
      primaryGhost: colors.primary700,
      tab: colors.neutral600,
    },
    borders: {
      primary: colors.cream50,
      primaryGhost: colors.primary700,
      tab: colors.neutral300,
    },
    iconColors: {
      primary: colors.cream50,
      primaryGhost: colors.primary700,
    },
    sizes: {
      small: '32px',
      medium: '40px',
      large: '48px',
    },
    padding: {
      small: '6px 16px',
      medium: '10px 16px',
      large: '14px 16px',
    },
  },
  buttons: {
    sizes: {
      small: '32px',
      medium: '40px',
      large: '48px',
    },
  },
  shadows,
}
export const theme = {
  colors: {
    primary30: '#1B8656',
    primary40: '#2AAF71',
    primary45: '#31B97A',
    primary90: '#D2FDE6',
    primary95: '#EFFCF5',
    neutral10: '#181B1A',
    neutral30: '#49504D',
    neutral40: '#626A66',
    neutral50: '#7A8580',
    neutral60: '#959D99',
    neutral70: '#AFB6B3',
    neutral90: '#E4E7E6',
    neutral95: '#F5F5F5',
    neutral98: '#E4E7E6',
    blue45: '#224AC0',
    blue55: '#4166D4',
    blue96: '#EEF0FB',
    red45: '#CC1010',
    red56: '#F52929',
    red95: '#FFE2E2',
    white: '#FFFFFF',
  },
  radius: {
    pill: '6px',
    button: '8px',
    card: '8px',
    input: '8px',
    filterButton: '32px',
    popup: '20px',
  },
}

export const breakpoints = {
  values: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
  },
  px: {
    xs: '0',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
}

export const themeOptions = {
  palette: {
    primary: {
      main: '#2AAF71',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4166D4',
      contrastText: '#ffffff',
    },
    error: {
      main: '#F52929',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#EA693D',
      contrastText: '#ffffff',
    },
  },
  breakpoints,
  typography: {
    fontSize: 16,
    h1: {
      fontSize: '3.0625rem',
      fontWeight: 700,
      lineHeight: 1.31,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.4375rem',
      fontWeight: 700,
      lineHeight: 1.53,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontSize: '1.9375rem',
      fontWeight: 700,
      lineHeight: 1.29,
    },
    h4: {
      fontSize: '1.5625rem',
      fontWeight: 700,
      lineHeight: 1.28,
      letterSpacing: '0em',
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 8,
  },
  system: {
    ...system,
  },
  custom: {
    ...theme,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: system.shadows.elevation4,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${system.colors.primary300}`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${system.colors.red300}`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          width: '100%',
          ':focus-within': {
            boxShadow: `0px 0px 0px 4px ${system.colors.primary100}`,
            outlined: 'none',
          },
          ':focus-within .Mui-error .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0px 0px 0px 4px #FEE4E2',
          },
          input: {
            padding: '10px 12px',
            fontSize: '14px',
          },
          '& .MuiInputAdornment-outlined': {
            margin: 0,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // Name of the slot
        root: {
          // Some CSS
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 'normal',
          background: theme.colors.white,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.colors.neutral50,
          color: theme.colors.neutral30,
          ':hover': {
            background: system.colors.neutral300,
            color: theme.colors.neutral30,
          },
          ':disabled': {
            background: theme.colors.neutral50,
            color: theme.colors.neutral30,
            opacity: 0.5,
          },
        },
        sizeLarge: {
          padding: '12px 20px',
          height: '48px',
        },
        sizeMedium: {
          height: '40px',
          padding: '10px 16px',
        },
        sizeSmall: {
          height: '32px',
          padding: '6px 14px',
        },
        containedPrimary: {
          background: system.colors.primary700,
          color: theme.colors.white,
          borderColor: system.colors.primary700,
          boxShadow: system.shadows.elevation1,
          ':hover': {
            background: system.colors.primary800,
            color: theme.colors.white,
          },
          ':disabled': {
            background: system.colors.primary700,
            color: theme.colors.white,
            opacity: 0.5,
          },
        },
        outlinedPrimary: {
          background: theme.colors.white,
          color: system.colors.primary700,
          borderColor: system.colors.primary700,
          ':hover': {
            borderColor: system.colors.primary800,
            background: theme.colors.white,
            color: system.colors.primary800,
          },
          ':disabled': {
            background: theme.colors.white,
            color: system.colors.primary700,
            opacity: 0.5,
          },
        },
        containedSecondary: {
          background: colors.red600,
          borderColor: colors.red600,
          color: theme.colors.white,
          boxShadow: system.shadows.elevation1,
          ':hover': {
            background: colors.red700,
            color: theme.colors.white,
          },
          ':disabled': {
            background: colors.red600,
            color: theme.colors.white,
            opacity: 0.5,
          },
        },
        outlinedSecondary: {
          background: colors.white,
          color: colors.red700,
          borderColor: colors.red700,
          ':hover': {
            background: colors.red300,
            color: colors.red700,
            borderColor: colors.red700,
          },
          ':disabled': {
            background: colors.white,
            color: colors.red700,
            opacity: 0.5,
          },
        },
      },
    },
  },
}
